import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ShieldCheck, ShieldOff, Loader2, CreditCard, ExternalLink, XCircle, RefreshCw } from "lucide-react";
import OrgProfilePanel from "@/components/OrgProfilePanel";
import TeamPanel from "@/components/TeamPanel";

export default function Settings() {
  const { user, logout, setUser } = useAuth();
  const [twoFA, setTwoFA] = useState({ enabled: !!user?.two_factor_enabled });
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupData, setSetupData] = useState(null); // { qr_code, secret }
  const [verifyCode, setVerifyCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [portalBusy, setPortalBusy] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [reactivateBusy, setReactivateBusy] = useState(false);

  const loadSubscription = async () => {
    setSubLoading(true);
    try {
      const res = await api.get("/payments/subscription");
      setSubscription(res.data.subscription);
    } catch (err) {
      setSubscription(null);
    } finally {
      setSubLoading(false);
    }
  };

  useEffect(() => {
    loadSubscription();
  }, []);

  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const res = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = res.data.portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing portal");
      setPortalBusy(false);
    }
  };

  const confirmCancel = async () => {
    setCancelBusy(true);
    try {
      const res = await api.post("/payments/cancel");
      setSubscription(res.data.subscription);
      setCancelOpen(false);
      toast.success("Subscription cancellation scheduled. Access continues until period end.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setCancelBusy(false);
    }
  };

  const reactivate = async () => {
    setReactivateBusy(true);
    try {
      const res = await api.post("/payments/reactivate");
      setSubscription(res.data.subscription);
      toast.success("Subscription reactivated. You'll be billed as usual.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reactivate subscription");
    } finally {
      setReactivateBusy(false);
    }
  };

  const formatDate = (unix) => {
    if (!unix) return "";
    return new Date(unix * 1000).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  };

  const hasPaidTier = user?.subscription_tier && user.subscription_tier !== "free";
  const canCancel = subscription && !subscription.cancel_at_period_end &&
    ["active", "trialing", "past_due"].includes(subscription.status);
  const isCanceling = subscription && subscription.cancel_at_period_end;

  const renderCancelSection = () => {
    if (subLoading) {
      return (
        <div className="flex items-center gap-2 text-sm text-[#5E5E5E]">
          <Loader2 className="w-4 h-4 animate-spin"/> Loading subscription…
        </div>
      );
    }
    if (isCanceling) {
      return (
        <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="cancel-scheduled">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#C1654B]/10 text-[#C1654B] mb-2">
              Cancellation scheduled
            </div>
            <p className="text-sm text-[#1A1A1A] max-w-md leading-relaxed">
              Your subscription ends on{" "}
              <b>{formatDate(subscription.current_period_end)}</b>.
              You'll keep full access until then and won't be charged again.
            </p>
          </div>
          <Button
            onClick={reactivate}
            disabled={reactivateBusy}
            data-testid="reactivate-subscription-btn"
            className="bg-[#1C3F35] text-white hover:bg-[#1A362D]"
          >
            {reactivateBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : (
              <><RefreshCw className="w-4 h-4 mr-2"/>Resume subscription</>
            )}
          </Button>
        </div>
      );
    }
    if (canCancel) {
      return (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-heading text-base font-medium text-[#1A1A1A]">Cancel subscription</h3>
            <p className="text-[#5E5E5E] text-sm mt-1 max-w-md leading-relaxed">
              One-click cancel. You'll keep access until the end of your current billing period.
              {subscription?.status === "trialing" && subscription?.trial_end && (
                <> Trial ends {formatDate(subscription.trial_end)} — cancel before then to avoid any charge.</>
              )}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setCancelOpen(true)}
            data-testid="cancel-subscription-open"
            className="border-[#C1654B] text-[#C1654B] hover:bg-[#C1654B] hover:text-white"
          >
            <XCircle className="w-4 h-4 mr-2"/>Cancel subscription
          </Button>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    api.get("/2fa/status").then((r) => setTwoFA({ enabled: r.data.enabled })).catch(() => {});
  }, []);

  const refreshUser = async () => {
    const me = await api.get("/auth/me");
    localStorage.setItem("gf_user", JSON.stringify(me.data));
    setUser(me.data);
  };

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await api.post("/2fa/setup");
      setSetupData(res.data);
      setSetupOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Setup failed");
    } finally {
      setBusy(false);
    }
  };

  const verifySetup = async () => {
    if (verifyCode.length !== 6) { toast.error("Enter 6 digits"); return; }
    setBusy(true);
    try {
      await api.post("/2fa/verify", { code: verifyCode });
      toast.success("2FA enabled!");
      setSetupOpen(false);
      setSetupData(null);
      setVerifyCode("");
      setTwoFA({ enabled: true });
      await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const disable2FA = async () => {
    if (disableCode.length !== 6) { toast.error("Enter 6 digits"); return; }
    setBusy(true);
    try {
      await api.post("/2fa/disable", { code: disableCode });
      toast.success("2FA disabled");
      setDisableOpen(false);
      setDisableCode("");
      setTwoFA({ enabled: false });
      await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="settings-page">
      <header>
        <div className="label-eyebrow mb-2">Account</div>
        <h1 className="font-heading font-light text-4xl md:text-5xl tracking-tighter">Settings</h1>
      </header>

      <div className="bg-white border border-[#E6E4DF] rounded-lg p-8 space-y-5">
        <div>
          <Label>Full name</Label>
          <Input value={user?.full_name || ""} readOnly className="mt-2 bg-[#F9F8F6] border-[#E6E4DF]"/>
        </div>
        <div>
          <Label>Email</Label>
          <div className="flex items-center gap-3 mt-2">
            <Input value={user?.email || ""} readOnly className="bg-[#F9F8F6] border-[#E6E4DF]"/>
            {user?.email_verified ? (
              <span className="text-xs px-2 py-1 rounded-full bg-[#E8ECEB] text-[#1C3F35] font-medium">Verified</span>
            ) : (
              <span className="text-xs px-2 py-1 rounded-full bg-[#C1654B]/10 text-[#C1654B] font-medium">Unverified</span>
            )}
          </div>
        </div>
        <div>
          <Label>Organization type</Label>
          <Input value={user?.organization_type || ""} readOnly className="mt-2 bg-[#F9F8F6] border-[#E6E4DF] capitalize"/>
        </div>
        <div>
          <Label>Subscription tier</Label>
          <div className="mt-2 flex items-center gap-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-[#1C3F35] text-white">
              {user?.subscription_tier}
            </span>
            <a href="/pricing" className="text-sm text-[#1C3F35] underline">Manage subscription</a>
          </div>
        </div>
      </div>

      {/* Org profile */}
      <OrgProfilePanel />

      {/* Billing */}
      <div className="bg-white border border-[#E6E4DF] rounded-lg p-8" data-testid="settings-billing">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#1C3F35]"/>
              <h2 className="font-heading text-xl font-medium">Billing &amp; payment method</h2>
            </div>
            <p className="text-[#5E5E5E] text-sm mt-2 max-w-md leading-relaxed">
              {hasPaidTier
                ? "Update your card, download invoices, or manage your subscription in the Stripe billing portal."
                : "Start a plan first to manage billing. Every plan includes a 7-day free trial."}
            </p>
          </div>
          {hasPaidTier ? (
            <Button
              onClick={openBillingPortal}
              disabled={portalBusy}
              data-testid="billing-portal-btn"
              className="bg-[#1C3F35] text-white hover:bg-[#1A362D]"
            >
              {portalBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : (
                <>Manage billing<ExternalLink className="w-4 h-4 ml-2"/></>
              )}
            </Button>
          ) : (
            <a
              href="/pricing"
              data-testid="billing-view-plans"
              className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-[#1C3F35] text-white hover:bg-[#1A362D] transition-colors"
            >
              View plans<ExternalLink className="w-4 h-4 ml-2"/>
            </a>
          )}
        </div>

        {hasPaidTier && (
          <div className="mt-6 pt-6 border-t border-[#E6E4DF]" data-testid="settings-cancel-section">
            {renderCancelSection()}
          </div>
        )}
      </div>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Cancel your subscription?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#1A1A1A] leading-relaxed">
            <p>Before you go, here's what will happen:</p>
            <ul className="space-y-2 pl-1">
              <li className="flex items-start gap-2">
                <span className="text-[#1C3F35] mt-1">·</span>
                <span>Your subscription stays <b>active until the end of your current billing period</b>
                {subscription?.current_period_end && (
                  <> — <b>{formatDate(subscription.current_period_end)}</b></>
                )}.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#1C3F35] mt-1">·</span>
                <span>No future payments will be charged.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#1C3F35] mt-1">·</span>
                <span>No partial or prorated refunds will be issued.</span>
              </li>
            </ul>
            <p className="text-xs text-[#5E5E5E] mt-3">
              See our <a href="/refund-policy" className="underline underline-offset-2 hover:text-[#1C3F35]">Refund &amp; Cancellation Policy</a>.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)} data-testid="cancel-dialog-back">
              Keep subscription
            </Button>
            <Button
              onClick={confirmCancel}
              disabled={cancelBusy}
              data-testid="cancel-dialog-confirm"
              className="bg-[#C1654B] text-white hover:bg-[#A0533E]"
            >
              {cancelBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Yes, cancel"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team seats */}
      <TeamPanel />

      {/* 2FA */}
      <div className="bg-white border border-[#E6E4DF] rounded-lg p-8" data-testid="settings-2fa">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              {twoFA.enabled ? <ShieldCheck className="w-5 h-5 text-[#1C3F35]"/> : <ShieldOff className="w-5 h-5 text-[#5E5E5E]"/>}
              <h2 className="font-heading text-xl font-medium">Two-factor authentication</h2>
              {twoFA.enabled && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1C3F35] text-white font-bold uppercase tracking-widest">On</span>
              )}
            </div>
            <p className="text-[#5E5E5E] text-sm mt-2 max-w-md">
              Protect your account with an authenticator app (Google Authenticator, Authy, 1Password).
            </p>
          </div>
          {twoFA.enabled ? (
            <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="2fa-disable-open" className="border-[#C1654B] text-[#C1654B] hover:bg-[#C1654B] hover:text-white">Disable</Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-md">
                <DialogHeader><DialogTitle className="font-heading text-2xl">Disable two-factor</DialogTitle></DialogHeader>
                <p className="text-[#5E5E5E] text-sm">Enter a current code from your authenticator to disable 2FA.</p>
                <Input
                  value={disableCode}
                  onChange={(e)=>setDisableCode(e.target.value.replace(/\D/g,"").slice(0,6))}
                  data-testid="2fa-disable-code"
                  placeholder="000000" maxLength={6}
                  className="text-center text-2xl tracking-[0.4em] font-mono bg-white border-[#E6E4DF]"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={()=>setDisableOpen(false)}>Cancel</Button>
                  <Button onClick={disable2FA} disabled={busy} data-testid="2fa-disable-confirm" className="bg-[#C1654B] text-white hover:bg-[#A0533E]">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Disable 2FA"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          ) : (
            <Button onClick={startSetup} disabled={busy} data-testid="2fa-setup-start" className="bg-[#1C3F35] text-white hover:bg-[#1A362D]">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Enable 2FA"}
            </Button>
          )}
        </div>

        <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
          <DialogContent className="bg-white max-w-md" data-testid="2fa-setup-dialog">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Set up two-factor</DialogTitle></DialogHeader>
            {setupData && (
              <div className="space-y-4">
                <p className="text-sm text-[#5E5E5E]">
                  1. Scan this QR with Google Authenticator, Authy, or 1Password.<br/>
                  2. Enter the 6-digit code below to confirm.
                </p>
                <div className="flex justify-center bg-white p-4 rounded-lg border border-[#E6E4DF]">
                  <img src={setupData.qr_code} alt="2FA QR" className="w-56 h-56" data-testid="2fa-qr"/>
                </div>
                <details className="text-xs text-[#5E5E5E]">
                  <summary className="cursor-pointer">Can't scan? Enter this key manually</summary>
                  <div className="mt-2 font-mono bg-[#F9F8F6] p-2 rounded break-all">{setupData.secret}</div>
                </details>
                <Input
                  value={verifyCode}
                  onChange={(e)=>setVerifyCode(e.target.value.replace(/\D/g,"").slice(0,6))}
                  data-testid="2fa-setup-code"
                  placeholder="Enter 6-digit code" maxLength={6}
                  className="text-center text-2xl tracking-[0.4em] font-mono bg-white border-[#E6E4DF]"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={()=>setSetupOpen(false)}>Cancel</Button>
                  <Button onClick={verifySetup} disabled={busy} data-testid="2fa-setup-verify" className="bg-[#1C3F35] text-white hover:bg-[#1A362D]">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Verify & enable"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E6E4DF] rounded-lg p-8">
        <h2 className="font-heading text-xl font-medium mb-2">Sign out</h2>
        <p className="text-[#5E5E5E] text-sm mb-4">Sign out of Beacon of Grants on this device.</p>
        <Button onClick={logout} variant="outline" data-testid="settings-logout" className="border-[#C1654B] text-[#C1654B] hover:bg-[#C1654B] hover:text-white">
          Sign out
        </Button>
      </div>
    </div>
  );
}
